import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import supabase from '../lib/supabase'
import Modal from './common/Modal'

function ModernServicesCarousel() {
  const [services, setServices] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedService, setSelectedService] = useState(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    fetchServices()
  }, [])

  useEffect(() => {
    if (services.length > 0) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length)
      }, 5000)
      return () => clearInterval(timer)
    }
  }, [services.length])

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('codevaro_services')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })

      if (error) throw error
      setServices(data || [])
    } catch (error) {
      console.error('Error fetching services:', error)
    } finally {
      setLoading(false)
    }
  }

  const openServiceModal = (service) => {
    setSelectedService(service)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedService(null)
  }

  // Smooth scroll to booking section
  const scrollToBooking = () => {
    closeModal()
    const bookingSection = document.getElementById('booking')
    if (bookingSection) {
      bookingSection.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }

  // Smooth scroll to contact section
  const scrollToContact = () => {
    closeModal()
    const contactSection = document.getElementById('contact')
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }

  const goToSlide = (index) => {
    setCurrentIndex(index)
  }

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % services.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + services.length) % services.length)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Δεν υπάρχουν διαθέσιμες υπηρεσίες αυτή τη στιγμή.</p>
      </div>
    )
  }

  const currentService = services[currentIndex]

  return (
    <>
      <div className="relative bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl overflow-hidden shadow-2xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600 to-purple-600"></div>
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full opacity-20"></div>
          <div className="absolute bottom-10 right-10 w-24 h-24 bg-white rounded-full opacity-15"></div>
        </div>

        <div className="relative z-10 p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center min-h-[400px]">
            {/* Service Content */}
            <motion.div
              key={currentService.id}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Service Icon */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  {currentService.icon && (
                    <SafeIcon 
                      icon={FiIcons[currentService.icon]} 
                      className="w-8 h-8 text-white" 
                    />
                  )}
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {currentService.title}
                  </h3>
                  <p className="text-blue-600 font-medium">Υπηρεσία #{currentIndex + 1}</p>
                </div>
              </div>

              {/* Service Description */}
              <p className="text-lg text-gray-700 leading-relaxed">
                {currentService.description}
              </p>

              {/* Service Features */}
              {currentService.features && currentService.features.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Βασικά χαρακτηριστικά:</h4>
                  <ul className="space-y-1">
                    {currentService.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-gray-700">
                        <SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4 text-green-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => openServiceModal(currentService)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  Μάθε περισσότερα
                </button>
                <button
                  onClick={scrollToContact}
                  className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300"
                >
                  Επικοινώνησε μαζί μας
                </button>
              </div>
            </motion.div>

            {/* Service Visual */}
            <motion.div
              key={`visual-${currentService.id}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              {currentService.image_url ? (
                <img
                  src={currentService.image_url}
                  alt={currentService.title}
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
              ) : (
                <div className="w-full h-80 bg-gradient-to-br from-blue-200 to-purple-200 rounded-2xl flex items-center justify-center shadow-xl">
                  <SafeIcon 
                    icon={FiIcons[currentService.icon] || FiIcons.FiCode} 
                    className="w-24 h-24 text-blue-600" 
                  />
                </div>
              )}
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-bounce"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-pink-400 rounded-full animate-pulse"></div>
            </motion.div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-between items-center mt-8">
            {/* Dots Indicator */}
            <div className="flex space-x-2">
              {services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-blue-600 w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {/* Arrow Controls */}
            <div className="flex space-x-2">
              <button
                onClick={prevSlide}
                className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiIcons.FiChevronLeft} className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={nextSlide}
                className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiIcons.FiChevronRight} className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Service Detail Modal */}
      <Modal isOpen={showModal} onClose={closeModal}>
        {selectedService && (
          <div className="space-y-6">
            {/* Modal Header */}
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                {selectedService.icon && (
                  <SafeIcon 
                    icon={FiIcons[selectedService.icon]} 
                    className="w-6 h-6 text-white" 
                  />
                )}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedService.title}</h3>
                <p className="text-gray-600">Λεπτομερείς πληροφορίες</p>
              </div>
            </div>

            {/* Service Image */}
            {selectedService.image_url && (
              <img
                src={selectedService.image_url}
                alt={selectedService.title}
                className="w-full h-48 object-cover rounded-xl"
              />
            )}

            {/* Service Description */}
            <p className="text-gray-700 leading-relaxed">{selectedService.description}</p>

            {/* Service Features */}
            {selectedService.features && selectedService.features.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Χαρακτηριστικά υπηρεσίας:</h4>
                <ul className="space-y-2">
                  {selectedService.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <SafeIcon icon={FiIcons.FiCheck} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pricing */}
            {selectedService.price && (
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-sm text-gray-600">Τιμή από:</p>
                <p className="text-2xl font-bold text-blue-600">€{selectedService.price}</p>
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                onClick={scrollToBooking}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiIcons.FiCalendar} className="w-4 h-4" />
                Κλείσε Ραντεβού
              </button>
              <button
                onClick={scrollToContact}
                className="flex-1 border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiIcons.FiMessageCircle} className="w-4 h-4" />
                Επικοινωνία
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

export default ModernServicesCarousel